/**
 * Firebase Web SDK init + auth helpers (modular SDK only). Auth ONLY - the typed
 * API client lives in `./api`.
 *
 * MULTI-TENANT: users are signed into a per-client Identity Platform TENANT (not
 * the project's default IdP). The tenant id is not known at build time - it is
 * fetched at runtime from `GET /api/auth/config` (per <slug>.gum.nextnovate.com
 * deployment) and pinned onto the auth instance via `setAuthTenant()` BEFORE any
 * sign-in call. `auth.tenantId` is sticky per instance but NOT persisted across
 * page loads, so `loadAuthConfig()` re-applies it on every page load before the
 * auth gate renders.
 *
 * Env vars (baked at `vite build`, the project's Firebase web config):
 *   VITE_FIREBASE_API_KEY
 *   VITE_FIREBASE_AUTH_DOMAIN
 *   VITE_FIREBASE_PROJECT_ID
 *
 * Init is LAZY and GUARDED: in dev (localhost without VITE_ vars) the SDK is never
 * initialised, so importing this module must not crash - `getFirebaseAuth()`
 * returns null and callers fall back to the no-auth dev path.
 */

import { getApps, initializeApp, type FirebaseApp } from 'firebase/app'
import {
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  type Auth,
  type User,
} from 'firebase/auth'

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
}

/** True only when all three build-time Firebase vars are present. */
export function isFirebaseConfigured(): boolean {
  return Boolean(config.apiKey && config.authDomain && config.projectId)
}

let cachedAuth: Auth | null = null

/**
 * Lazily initialise Firebase and return the auth instance, or null when Firebase
 * is not configured (dev mode). Guarded so a bare import never crashes.
 */
export function getFirebaseAuth(): Auth | null {
  if (!isFirebaseConfigured()) {
    return null
  }
  if (cachedAuth) {
    return cachedAuth
  }
  const app: FirebaseApp =
    getApps().length > 0 ? getApps()[0]! : initializeApp(config)
  cachedAuth = getAuth(app)
  return cachedAuth
}

const provider = new GoogleAuthProvider()

/**
 * Pin the auth instance to a per-client Identity Platform tenant. MUST be called
 * before `signInWithGoogle()` and re-applied on every page load, since
 * `auth.tenantId` is not persisted. Idempotent; a no-op when Firebase is absent.
 */
export function setAuthTenant(tenantId: string): void {
  const auth = getFirebaseAuth()
  if (auth) {
    auth.tenantId = tenantId
  }
}

/**
 * Google sign-in via popup ONLY. `signInWithRedirect` relies on a cross-origin
 * iframe to the authDomain, which third-party-storage partitioning blocks on
 * custom subdomains (our <slug>.gum.nextnovate.com portals). Popup keeps the flow
 * first-party. Must be invoked synchronously from the click handler so the popup
 * is not blocked.
 */
export async function signInWithGoogle(): Promise<User> {
  const auth = getFirebaseAuth()
  if (!auth) {
    throw new Error('Firebase is not configured')
  }
  const cred = await signInWithPopup(auth, provider)
  return cred.user
}

export async function signOutCurrentUser(): Promise<void> {
  const auth = getFirebaseAuth()
  if (auth) {
    await signOut(auth)
  }
}

export { onAuthStateChanged }
export type { User }
